import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, doc, getDoc } from '../../firebase';
import { FaShoppingCart, FaWallet, FaBoxOpen, FaCheckCircle, FaStar, FaBullhorn, FaHandshake, FaTools, FaFlagCheckered } from 'react-icons/fa';

// One shared, promise-memoized read of the single public activity_feed doc, so
// navigating around the app doesn't re-read it. Load-once, not realtime (cheap).
let feedPromise = null;
function getActivityFeed() {
    if (!db) return Promise.resolve([]);
    if (!feedPromise) {
        feedPromise = getDoc(doc(db, 'public/data/activity_feed', 'recent'))
            .then(s => (s.exists() && Array.isArray(s.data().events)) ? s.data().events : [])
            .catch(() => { feedPromise = null; return []; });
    }
    return feedPromise;
}

// Bright icon + colored glow per type — the glow is what gives the dark panel its
// "colour depth" (each activity type lights up in its own colour).
const TYPES = {
    new:       { Icon: FaShoppingCart, color: '#A5B4FC', glow: 'rgba(129,140,248,.55)', bg: 'rgba(129,140,248,.16)' },
    paid:      { Icon: FaWallet,       color: '#93C5FD', glow: 'rgba(96,165,250,.55)',  bg: 'rgba(96,165,250,.16)' },
    delivered: { Icon: FaBoxOpen,      color: '#5EEAD4', glow: 'rgba(45,212,191,.55)',  bg: 'rgba(45,212,191,.16)' },
    completed: { Icon: FaCheckCircle,  color: '#6EE7B7', glow: 'rgba(52,211,153,.55)',  bg: 'rgba(52,211,153,.16)' },
    review:    { Icon: FaStar,         color: '#FCD34D', glow: 'rgba(251,191,36,.55)',  bg: 'rgba(251,191,36,.16)' },
    // Jobboard events — written only by the onPublicJobWritten Cloud Function.
    job_new:     { Icon: FaBullhorn,      color: '#C4B5FD', glow: 'rgba(167,139,250,.55)', bg: 'rgba(167,139,250,.16)' },
    job_taken:   { Icon: FaHandshake,     color: '#F9A8D4', glow: 'rgba(244,114,182,.55)', bg: 'rgba(244,114,182,.16)' },
    job_working: { Icon: FaTools,         color: '#FDBA74', glow: 'rgba(251,146,60,.55)',  bg: 'rgba(251,146,60,.16)' },
    job_done:    { Icon: FaFlagCheckered, color: '#86EFAC', glow: 'rgba(74,222,128,.55)',  bg: 'rgba(74,222,128,.16)' },
};

const timeAgo = (ts) => {
    if (!ts) return 'เมื่อครู่';
    const m = Math.floor((Date.now() - ts) / 60000);
    if (m < 1) return 'เมื่อครู่';
    if (m < 60) return `${m} นาทีที่แล้ว`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} ชม.ที่แล้ว`;
    if (h < 48) return 'เมื่อวานนี้';
    return `${Math.floor(h / 24)} วันที่แล้ว`;
};

const baht = (n) => '฿' + new Intl.NumberFormat('th-TH').format(Number(n) || 0);
const who = (e) => (e.admin ? `แอดมิน ${e.admin}` : 'ทีมงาน Fastlane');

const truncate = (s, n = 40) => (s && s.length > n ? s.slice(0, n) + '…' : (s || ''));

const labelFor = (e) => {
    const L = truncate(e.label);
    switch (e.type) {
        case 'review': return `คุณ ${e.name} รีวิว ${e.rating || 5}★${L ? ' · ' + L : ''}`;
        case 'completed': return `${L} เสร็จสิ้นแล้ว`;
        case 'delivered': return `ส่งมอบ ${L} เรียบร้อย`;
        case 'paid': return `คุณ ${e.name} ชำระเงิน ${L} สำเร็จ`;
        case 'job_new': return `มีงานใหม่ ${L} · ${e.jobType === 'budget' && e.budget ? 'งบ ' + baht(e.budget) : 'ขอใบเสนอราคา'}`;
        case 'job_taken': return `${who(e)} รับงาน ${L}${e.jobType === 'budget' && e.budget ? ' · ' + baht(e.budget) : ''}`;
        case 'job_working': return `${who(e)} กำลังดำเนินการ ${L}`;
        case 'job_done': return `งาน ${L} เสร็จแล้ว · โดย${who(e)}`;
        default: return `คุณ ${e.name} สั่งซื้อ ${L}`;
    }
};

function ActivityTicker() {
    const [events, setEvents] = useState([]);

    useEffect(() => {
        let mounted = true;
        // Drop types this bundle doesn't know (instead of mislabelling them as "สั่งซื้อ").
        getActivityFeed().then(evs => { if (mounted) setEvents((evs || []).filter(e => e && TYPES[e.type]).slice(0, 15)); });
        return () => { mounted = false; };
    }, []);

    if (!events.length) return null;
    const loop = [...events, ...events]; // duplicated for a seamless marquee

    return (
        <div className="w-full mt-4 mb-12">
            <div className="at-panel relative overflow-hidden">
                <div className="at-glow-a" aria-hidden="true"></div>
                <div className="at-glow-b" aria-hidden="true"></div>
                <div className="at-accent" aria-hidden="true"></div>

                <div className="relative z-10 flex items-center px-5 sm:px-8 pt-4 pb-3">
                    <div className="flex items-center gap-2.5">
                        <span className="at-dot relative flex h-2.5 w-2.5" aria-hidden="true">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"></span>
                        </span>
                        <span className="text-[15px] font-bold text-white tracking-wide">กิจกรรมล่าสุด</span>
                        <span className="at-live text-[10px] font-bold tracking-widest rounded-full px-2 py-[3px]">LIVE</span>
                    </div>
                </div>

                <div className="at-divider" aria-hidden="true"></div>

                <div className="at-mask relative z-10 overflow-hidden py-3.5">
                    <div className="at-track flex gap-3 w-max px-4">
                        {loop.map((e, i) => {
                            const t = TYPES[e.type];
                            const Icon = t.Icon;
                            const body = (
                                <>
                                    <span className="flex items-center justify-center w-9 h-9 rounded-full flex-none"
                                        style={{ background: t.bg, color: t.color, boxShadow: `0 0 16px ${t.glow}, inset 0 0 0 1px ${t.glow}` }}>
                                        <Icon className="w-4 h-4" />
                                    </span>
                                    <span className="text-[13.5px] font-medium" style={{ color: '#E7ECF6' }}>{labelFor(e)}</span>
                                    <span className="text-[11px] flex-none" style={{ color: '#8592AE' }}>{timeAgo(e.ts || (typeof e.at === 'number' ? e.at : e.at?.toMillis?.()))}</span>
                                </>
                            );
                            const pillCls = 'at-pill flex items-center gap-2.5 pl-2 pr-4 py-2 rounded-full whitespace-nowrap';
                            // Job events link to the public job page (it shows "งานนี้ปิดรับแล้ว" if gone).
                            return (e.jobId && /^[A-Za-z0-9]{1,40}$/.test(e.jobId))
                                ? <Link key={i} to={`/jobs/${e.jobId}`} className={pillCls}>{body}</Link>
                                : <div key={i} className={pillCls}>{body}</div>;
                        })}
                    </div>
                </div>

                <style>{`
                    .at-panel{
                        background:linear-gradient(135deg,#141B31 0%,#1C2444 52%,#0F162B 100%);
                        border:1px solid rgba(255,255,255,.72);
                        box-shadow:0 26px 60px -22px rgba(99,102,241,.55), 0 12px 30px -12px rgba(2,6,23,.7), inset 0 1px 0 rgba(255,255,255,.07);
                    }
                    .at-accent{position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,#8B5CF6 0%,#6366F1 45%,#22D3EE 100%);box-shadow:0 0 16px rgba(99,102,241,.6);z-index:2}
                    .at-glow-a{position:absolute;top:-70px;right:-50px;width:300px;height:220px;background:radial-gradient(circle,rgba(139,92,246,.55),transparent 65%);filter:blur(34px);pointer-events:none;z-index:0}
                    .at-glow-b{position:absolute;bottom:-80px;left:-40px;width:300px;height:220px;background:radial-gradient(circle,rgba(34,211,238,.4),transparent 65%);filter:blur(38px);pointer-events:none;z-index:0}
                    .at-live{background:rgba(16,185,129,.16);border:1px solid rgba(52,211,153,.45);color:#6EE7B7;box-shadow:0 0 12px rgba(16,185,129,.35)}
                    .at-dot .relative{box-shadow:0 0 10px rgba(52,211,153,.9)}
                    .at-divider{height:1px;background:linear-gradient(90deg,transparent,rgba(255,255,255,.12) 12%,rgba(255,255,255,.12) 88%,transparent)}
                    .at-pill{background:rgba(255,255,255,.055);border:1px solid rgba(255,255,255,.10);box-shadow:0 8px 20px -8px rgba(0,0,0,.55);transition:transform .2s ease,box-shadow .2s ease,background .2s ease}
                    .at-pill:hover{transform:translateY(-3px);background:rgba(255,255,255,.09);box-shadow:0 16px 28px -10px rgba(0,0,0,.65)}
                    .at-mask{-webkit-mask-image:linear-gradient(90deg,transparent,#000 5%,#000 95%,transparent);mask-image:linear-gradient(90deg,transparent,#000 5%,#000 95%,transparent)}
                    .at-track{animation:atScroll 48s linear infinite}
                    .at-mask:hover .at-track{animation-play-state:paused}
                    @keyframes atScroll{from{transform:translateX(0)}to{transform:translateX(-50%)}}
                `}</style>
            </div>
        </div>
    );
}

export default ActivityTicker;
